import { Menu as MuiMenu, MenuItem as MuiMenuItem, type MenuProps } from "@mui/material";
import { type ReactNode, useRef, useEffect, useState, type MouseEvent, type KeyboardEvent } from "react";
import { twMerge } from "tailwind-merge";

interface MenuItem {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
    className?: string;
}

interface UseMenuReturn {
    anchorEl: HTMLElement | null;
    isOpen: boolean;
    handleClick: (event: MouseEvent<HTMLElement>) => void;
    handleKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
    handleClose: () => void;
}

export function useMenu(): UseMenuReturn {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const isOpen = Boolean(anchorEl);

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            setAnchorEl(event.currentTarget);
        } else if (event.key === "Escape") {
            setAnchorEl(null);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return {
        anchorEl,
        isOpen,
        handleClick,
        handleKeyDown,
        handleClose,
    };
}

interface SharedMenuProps extends Omit<MenuProps, "open" | "anchorEl"> {
    isOpen: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    items: MenuItem[];
}

export function Menu({ isOpen, anchorEl, onClose, items, ...props }: SharedMenuProps) {
    const menuItemRef = useRef<HTMLLIElement>(null);
    const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
    const paperSlotProps = props.slotProps?.paper;
    const mergedSlotProps = {
        ...props.slotProps,
        paper:
            typeof paperSlotProps === "function"
                ? paperSlotProps
                : {
                      ...(paperSlotProps ?? {}),
                      className: twMerge(
                          "card-bg border border-primary/20 shadow-lg",
                          (paperSlotProps as { className?: string } | undefined)?.className,
                      ),
                  },
    };

    useEffect(() => {
        if (isOpen && menuItemRef.current) {
            menuItemRef.current.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        setPortalContainer(document.getElementById("app-theme-root"));
    }, []);

    return (
        <MuiMenu
            open={isOpen}
            anchorEl={anchorEl}
            container={portalContainer}
            onClose={onClose}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "right",
            }}
            slotProps={mergedSlotProps}
            {...props}
        >
            {items.map((item, index) => (
                <MuiMenuItem
                    key={item.label}
                    onClick={() => {
                        item.onClick();
                        onClose();
                    }}
                    ref={index === 0 ? menuItemRef : undefined}
                    tabIndex={0}
                    className={twMerge("text-dark font-[12px] hover:bg-primary/10", item.className)}
                >
                    {item.icon}
                    {item.label}
                </MuiMenuItem>
            ))}
        </MuiMenu>
    );
}

interface PopupMenuProps {
    children: ReactNode;
    className?: string;
    items: MenuItem[];
    isEnabled?: boolean;
    sx?: MenuProps["sx"];
}

export function PopupMenu({ children, className, items, isEnabled = true, sx }: PopupMenuProps) {
    const { anchorEl, isOpen, handleClick, handleKeyDown, handleClose } = useMenu();

    if (!isEnabled) {
        return <>{children}</>;
    }

    return (
        <>
            <button
                type="button"
                className={twMerge(
                    `relative inline-flex items-center cursor-pointer bg-transparent border-none p-0`,
                    className,
                )}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                aria-haspopup="true"
                aria-expanded={isOpen}
            >
                {children}
            </button>
            <Menu isOpen={isOpen} anchorEl={anchorEl} onClose={handleClose} items={items} sx={sx} />
        </>
    );
}
